/**
 * ValuationToolPage — DAT/Schwacke Bewertungs-Tool (Phase 3b Stub).
 *
 * Eingabe: Marke, Modell, Baujahr, km, Zustand, Fahrzeug-Typ.
 * Ausgabe: Trade-In (Händler-EK), Retail (Händler-VK), Privat-VK, Range, Faktoren.
 *
 * Aktuell läuft die Bewertung im MOCK-Modus (deterministisch via SHA256).
 * Sobald DAT- oder Schwacke-API-Keys gesetzt sind, wechselt das Backend automatisch
 * auf Live-Antworten — der Frontend-Code bleibt identisch.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, Calculator, Calendar as CalIcon, Gauge, Sparkles, ArrowRight, AlertTriangle, Bike } from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

const CONDITIONS = [
    { v: "like_new", l: "Wie neu (1)" },
    { v: "very_good", l: "Sehr gut (2)" },
    { v: "good", l: "Gut (3)" },
    { v: "fair", l: "Mittel (4)" },
    { v: "needs_work", l: "Reparaturbedürftig (5)" },
];
const VEHICLE_TYPES = [
    { v: "motorcycle", l: "Motorrad" },
    { v: "scooter", l: "Roller" },
    { v: "bicycle", l: "Fahrrad" },
    { v: "ebike", l: "E-Bike / Pedelec" },
];

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export default function ValuationToolPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const currentYear = new Date().getFullYear();

    const [form, setForm] = useState({
        brand: "", model: "", year: currentYear - 3, mileage_km: 15000,
        condition: "good", vehicle_type: "motorcycle", accidents: false,
    });

    // Feb-2026 (User-Wunsch 27.05.2026) — Pre-Fill via URL-Params, z.B. wenn
    // der User aus dem Inserat-Editor auf „GW-Quick-Preisermittlung" klickt:
    // ``/valuation?brand=BMW&model=R+1250+GS&year=2022&mileage=12000``.
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const updates = {};
        const b = searchParams.get("brand");
        const m = searchParams.get("model");
        const y = searchParams.get("year");
        const km = searchParams.get("mileage");
        if (b) updates.brand = b;
        if (m) updates.model = m;
        if (y && !isNaN(Number(y))) updates.year = Number(y);
        if (km && !isNaN(Number(km))) updates.mileage_km = Number(km);
        if (Object.keys(updates).length > 0) {
            setForm((p) => ({ ...p, ...updates }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "b2b" && user.role !== "admin") navigate("/");
        if (user) api.get("/valuation/history?limit=10").then((r) => setHistory(r.data?.items || [])).catch(() => {});
    }, [user, navigate, result]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.brand.trim() || !form.model.trim()) { toast.error("Marke und Modell sind Pflicht"); return; }
        setLoading(true);
        try {
            const r = await api.post("/valuation/estimate", {
                ...form,
                year: Number(form.year),
                mileage_km: Number(form.mileage_km),
            });
            setResult(r.data);
            toast.success(r.data.mocked ? "Mock-Bewertung erstellt (DAT/Schwacke-Keys fehlen)" : "Bewertung erstellt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bewertung fehlgeschlagen");
        }
        setLoading(false);
    };

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="valuation-tool-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
                    <Link to="/dashboard" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-dashboard">
                        ← Dashboard
                    </Link>
                    <div className="mt-3">
                        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight inline-flex items-center gap-2">
                            <Calculator className="w-7 h-7" /> DAT / Schwacke Bewertung
                        </h1>
                        <p className="text-sm text-gray-400 mt-1 truncate max-w-full">
                            Ankaufs-, VK- und Privatpreis-Schätzung für Motorräder & Bikes.
                            <span className="text-amber-300"> Aktuell MOCK</span> — bei DAT/Schwacke-API-Keys schaltet das Backend automatisch live.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 grid grid-cols-12 gap-4">
                {/* Form */}
                <form onSubmit={submit} className="col-span-12 lg:col-span-5 bg-white border border-gray-200 rounded-sm p-4 space-y-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700 border-b border-gray-200 pb-2 mb-2">Fahrzeug-Daten</div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Fahrzeug-Typ</label>
                            <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                                <SelectTrigger className="rounded-sm mt-1" data-testid="val-vehicle-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {VEHICLE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Zustand</label>
                            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                                <SelectTrigger className="rounded-sm mt-1" data-testid="val-condition"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CONDITIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Marke *</label>
                            <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-sm mt-1" placeholder="z. B. BMW" required data-testid="val-brand" />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Modell *</label>
                            <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-sm mt-1" placeholder="z. B. R1250GS" required data-testid="val-model" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-1"><CalIcon className="w-3 h-3" /> Baujahr</label>
                            <Input type="number" min="1950" max={currentYear + 1} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="rounded-sm mt-1 font-mono-num" required data-testid="val-year" />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-1"><Gauge className="w-3 h-3" /> km-Stand</label>
                            <Input type="number" min="0" max="2000000" value={form.mileage_km} onChange={(e) => setForm({ ...form, mileage_km: e.target.value })} className="rounded-sm mt-1 font-mono-num" required data-testid="val-mileage" />
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={form.accidents} onChange={(e) => setForm({ ...form, accidents: e.target.checked })} data-testid="val-accidents" />
                        <span><strong>Unfallschaden</strong> in der Historie (Wert −30 %)</span>
                    </label>

                    <Button type="submit" disabled={loading} className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider rounded-sm" data-testid="val-submit">
                        {loading ? "Berechne…" : <><Sparkles className="w-4 h-4 mr-1" /> Bewertung anfordern</>}
                    </Button>

                    <div className="bg-amber-50 border-l-4 border-amber-500 p-2 rounded-sm text-xs text-amber-900 flex gap-1.5 items-start">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>Ohne <code>DAT_API_KEY</code>/<code>SCHWACKE_API_KEY</code> liefern wir deterministische Mock-Werte (Algorithmus simuliert realistische Preise via Marke × Alter × km × Zustand).</span>
                    </div>
                </form>

                {/* Result */}
                <div className="col-span-12 lg:col-span-7 space-y-4">
                    {result ? (
                        <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-4" data-testid="val-result">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="font-display font-black text-lg inline-flex items-center gap-2">
                                    <Bike className="w-5 h-5" /> {result.vehicle.brand} {result.vehicle.model}
                                    <Badge className="text-[10px] rounded-sm uppercase font-bold bg-gray-100 text-gray-700">
                                        BJ {result.vehicle.year} · {Number(result.vehicle.mileage_km).toLocaleString("de-DE")} km
                                    </Badge>
                                </div>
                                <Badge className={`text-[10px] rounded-sm uppercase font-bold ${result.mocked ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                                    Quelle: {result.source}{result.mocked ? " (Mock)" : ""}
                                </Badge>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center" data-testid="val-trade-in">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Ankauf (Händler)</div>
                                    <div className="font-display font-black text-2xl mt-1 font-mono-num">{fmtEur(result.valuation.trade_in)}</div>
                                    <div className="text-[10px] text-gray-500 mt-1 font-mono-num">{fmtEur(result.valuation.trade_in_range[0])} – {fmtEur(result.valuation.trade_in_range[1])}</div>
                                </div>
                                <div className="bg-[#FFCC00]/15 border border-[#FFCC00] rounded-sm p-3 text-center" data-testid="val-retail">
                                    <div className="text-[10px] font-bold uppercase tracking-wider">Verkauf (Händler)</div>
                                    <div className="font-display font-black text-2xl mt-1 font-mono-num">{fmtEur(result.valuation.retail)}</div>
                                    <div className="text-[10px] text-gray-600 mt-1 font-mono-num">{fmtEur(result.valuation.retail_range[0])} – {fmtEur(result.valuation.retail_range[1])}</div>
                                </div>
                                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-center" data-testid="val-private">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Privat-VK</div>
                                    <div className="font-display font-black text-2xl mt-1 font-mono-num">{fmtEur(result.valuation.private)}</div>
                                    <div className="text-[10px] text-gray-500 mt-1">Endkunde</div>
                                </div>
                            </div>

                            <div className="bg-gray-50 border border-gray-200 rounded-sm p-2 text-[11px] font-mono-num grid grid-cols-2 md:grid-cols-4 gap-1">
                                <div><span className="text-gray-500">Neupreis-Basis</span><div className="font-bold">{fmtEur(result.factors.base_price_new)}</div></div>
                                <div><span className="text-gray-500">Alter</span><div className="font-bold">{result.factors.age_years} J. ({(result.factors.age_factor * 100).toFixed(0)}%)</div></div>
                                <div><span className="text-gray-500">km-Faktor</span><div className="font-bold">{(result.factors.km_factor * 100).toFixed(0)}%</div></div>
                                <div><span className="text-gray-500">Zustand</span><div className="font-bold">{(result.factors.condition_factor * 100).toFixed(0)}%</div></div>
                            </div>
                            <div className="text-[11px] text-gray-500">
                                Gültig bis <strong>{result.valid_until}</strong> · Request-ID: <code className="font-mono-num">{result.request_id.slice(0, 8)}</code>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white border border-gray-200 rounded-sm p-8 text-center">
                            <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <div className="font-display font-black text-lg">Noch keine Bewertung</div>
                            <p className="text-sm text-gray-500 mt-1">Fülle das Formular aus und klicke „Bewertung anfordern".</p>
                        </div>
                    )}

                    {/* History */}
                    {history.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-sm p-4">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Letzte Anfragen ({history.length})</div>
                            <div className="space-y-1.5">
                                {history.map((h) => (
                                    <div key={h.id} className="border-b border-gray-100 pb-1.5 text-xs flex items-center justify-between gap-2 last:border-b-0">
                                        <div className="truncate">
                                            <span className="font-bold">{h.vehicle?.brand} {h.vehicle?.model}</span>
                                            <span className="text-gray-500"> · BJ {h.vehicle?.year} · {Number(h.vehicle?.mileage_km || 0).toLocaleString("de-DE")} km</span>
                                        </div>
                                        <div className="font-mono-num font-bold shrink-0">{fmtEur(h.valuation_retail)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
